import { MISSING_TENANT_MESSAGE } from "../constants/Defaults.constants";
import { buildAuthConfig } from "./build-auth-config";
import { resolveTenantCode } from "./tenant-code";

/** Deploy-time manifest: Terraform writes public/config/tenants.json (not bundled in JS). */
export const TENANT_AUTH_MANIFEST_URL = `${process.env.PUBLIC_URL || ""}/config/tenants.json`;

/** Missing or unknown ?co= tenant code. */
export class InvalidTenantUrlError extends Error {
  constructor() {
    super(MISSING_TENANT_MESSAGE);
    this.name = "InvalidTenantUrlError";
  }
}

let authState = null;

async function fetchTenantManifest() {
  const response = await fetch(TENANT_AUTH_MANIFEST_URL, {
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to load tenant auth manifest (${response.status}) from ${TENANT_AUTH_MANIFEST_URL}`,
    );
  }

  return response.json();
}

/**
 * Loads /config/tenants.json, selects tenant by ?co= / sessionStorage, initializes OIDC settings.
 * Call once before rendering the app.
 */
export async function bootstrapAuth() {
  const tenantCode = resolveTenantCode();
  if (!tenantCode) {
    throw new InvalidTenantUrlError();
  }

  const manifest = await fetchTenantManifest();
  const tenant = manifest?.[tenantCode];

  if (!tenant) {
    throw new InvalidTenantUrlError();
  }

  authState = {
    tenantCode,
    ...buildAuthConfig(tenant),
  };

  return authState;
}

function assertBootstrapped() {
  if (!authState) {
    throw new Error("Auth is not initialized. Call bootstrapAuth() before using auth config.");
  }
}

export function getCognitoConfig() {
  assertBootstrapped();
  return authState.cognitoConfig;
}

export function getLoginRequest() {
  assertBootstrapped();
  return authState.loginRequest;
}

export function getUnifiedRequest() {
  assertBootstrapped();
  return authState.unifiedRequest;
}

export function getActiveTenantCode() {
  assertBootstrapped();
  return authState.tenantCode;
}
